import React from 'react';
import type { Theme } from '@mui/material';
import { Stack, styled, Typography, TypographyProps } from '@mui/material';
import { SystemStyleObject } from '@mui/system';

interface SlotProps extends Omit<TypographyProps, 'sx'> {
  lineThrough?: boolean;
  maxLines?: number;
  sx?: SystemStyleObject;
}

interface Props {
  topLine: string | number | React.ReactNode;
  bottomLine?: string | number | React.ReactNode;
  centerAlign?: boolean;
  gap?: 'small' | 'medium';
  nullableBottomLine?: boolean;
  slotProps?: {
    topLine?: SlotProps;
    bottomLine?: SlotProps;
  };
}

const isDefined = <T,>(v: T | undefined): v is T => v !== undefined;

const mergeSx = (items: Array<SystemStyleObject<Theme> | undefined>): SystemStyleObject<Theme> =>
  // @ts-ignore
  items.filter(isDefined);

const getLineClampSx = (maxLines?: number): SystemStyleObject<Theme> | undefined => {
  if (!maxLines || maxLines <= 0) return undefined;

  if (maxLines === 1) {
    return { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 };
  }

  return {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: maxLines,
    minWidth: 0,
  };
};

export const StackedTextCell = ({
  topLine,
  bottomLine,
  centerAlign,
  gap = 'medium',
  nullableBottomLine = false,
  slotProps = {},
}: Props) => {
  const { maxLines: topMaxLines, sx: topSx, ...topRest } = slotProps.topLine ?? {};
  const {
    maxLines: bottomMaxLines,
    sx: bottomSx,
    lineThrough: bottomLineThrough,
    ...bottomRest
  } = slotProps.bottomLine ?? {};

  const topClampSx = getLineClampSx(topMaxLines);
  const bottomClampSx = getLineClampSx(bottomMaxLines);

  return (
    <Stack
      textAlign={centerAlign ? 'center' : 'start'}
      gap={gap === 'medium' ? 0.5 : 0}
      justifyContent={centerAlign ? 'center' : undefined}
      alignItems={centerAlign ? 'center' : undefined}
      minWidth={0}
    >
      <TopLineTypography
        variant="subtitle2"
        component="span"
        isInline={topMaxLines !== undefined}
        {...topRest}
        sx={mergeSx([topClampSx, topSx])}
      >
        {topLine}
      </TopLineTypography>

      {(bottomLine || !nullableBottomLine) && (
        <Typography
          lineHeight={1.2}
          component="span"
          variant="subtitle2"
          {...bottomRest}
          sx={mergeSx([
            {
              textDecoration: bottomLine && bottomLineThrough ? 'line-through' : undefined,
            },
            bottomClampSx,
            bottomSx,
          ])}
        >
          {bottomLine ?? '-'}
        </Typography>
      )}
    </Stack>
  );
};

export const TopLineTypography = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'lineThrough' && prop !== 'isInline' && prop !== 'maxLines',
})<{ lineThrough?: boolean; isInline?: boolean; maxLines?: number }>(
  ({ theme, lineThrough, isInline }) => ({
    display: isInline ? 'inline' : undefined,
    lineHeight: 1.2,
    fontWeight: 600,
    color: theme.vars.palette.grey[900],
    textDecoration: lineThrough ? 'line-through' : undefined,
    '& *': isInline
      ? {
          // Ensure nested elements to not break the line clamp, by forcing them to be inline
          display: 'inline',
        }
      : undefined,
  })
);
